//-- copyright
// GPE customization (gov.ao)
//
// When OpenProject is embedded in a cross-site iframe (e.g. Nextcloud on gov.ao),
// the session cookie is Partitioned (CHIPS) to the top-level site. Following an
// attachment content link via a target="_blank" / new top-level navigation opens
// a context where that partitioned cookie is NOT sent, so the request to
// /api/v3/attachments/:id/content is anonymous and returns 404.
//
// This installs a capture-phase click interceptor (so it runs before any Angular
// component handler and cannot be defeated by stopPropagation). While embedded,
// clicks on attachment content links are fetched from the iframe document (same
// cookie partition -> the session cookie is sent) and saved as a blob. Covers the
// work package files tab, inline attachment links in rich text, and any other
// attachment link. Falls back to opening the link on failure. Modified clicks
// (Ctrl/Cmd/middle) and direct (non-embedded) access are left untouched.
//++

const ATTACHMENT_CONTENT_URL = /\/api\/v3\/attachments\/\d+\/content(?:[?#]|$)/;

function isEmbedded():boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin access check throws -> we are inside a cross-origin iframe.
    return true;
  }
}

function filenameFromDisposition(response:Response):string|null {
  const disposition = response.headers.get('Content-Disposition');
  if (!disposition) {
    return null;
  }

  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
  if (!match) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

async function fetchAndSave(url:string, suggestedName:string):Promise<void> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Attachment request failed with HTTP ${response.status}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filenameFromDisposition(response) || suggestedName || 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Installs a document-level capture-phase click listener that downloads
 * attachment content links via fetch+blob when OpenProject is embedded in an
 * iframe. No-op when not embedded.
 */
export function initializeEmbeddedAttachmentDownload():void {
  if (!isEmbedded()) {
    return;
  }

  document.addEventListener(
    'click',
    (evt:MouseEvent) => {
      if (evt.defaultPrevented) {
        return;
      }

      // Respect modified clicks (the user explicitly asked for a new tab/window).
      if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) {
        return;
      }

      const target = evt.target as HTMLElement | null;
      const link = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!link || !ATTACHMENT_CONTENT_URL.test(link.href)) {
        return;
      }

      // Run before any component handler and prevent the broken new-context navigation.
      evt.preventDefault();
      evt.stopPropagation();

      const suggested = link.getAttribute('download') || '';
      void fetchAndSave(link.href, suggested).catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('GPE: embedded attachment download failed, opening the link instead', e);
        window.open(link.href, '_blank');
      });
    },
    true, // capture phase
  );
}
